/**
 * Wire protocol for the party-loot live mutation path.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/LootMessages.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too.
 *
 * Loot rows are independent DDB items (PK = SESSION#<id>, SK = LOOT#<itemId>)
 * so the list can grow without rewriting the party doc. Add/Update/Remove
 * each target a single row; Update is NOT version-checked in this slice —
 * see LootMessages.cs for the rationale.
 */

import type { LootItem } from './party';

export const LootActions = {
  Add:    'loot/add',
  Update: 'loot/update',
  Remove: 'loot/remove',
} as const;

export const LootServerTypes = {
  LootAdded:    'loot/added',
  LootUpdated:  'loot/updated',
  LootRemoved:  'loot/removed',
  LootRejected: 'loot/rejected',
} as const;

/**
 * Add a new loot row. The server generates the id and addedAt so two clients
 * clicking "add" at the same millisecond can't collide. sessionId is taken
 * from the caller's connection row, not the payload.
 */
export interface LootAddRequest {
  action: typeof LootActions.Add;
  name: string;
  quantity: number;
  weight?: number;
  notes?: string;
  source?: string;
  identified: boolean;
  tags?: string[];
  reason?: string;
}

/**
 * Replace the mutable fields of an existing loot row by id. sessionId and
 * addedAt are preserved server-side; everything else comes from the payload.
 * Missing fields are treated as "clear", not "leave alone" — the wire format
 * is a full replace-by-id, so the client always ships the full intended row.
 */
export interface LootUpdateRequest {
  action: typeof LootActions.Update;
  id: string;
  name: string;
  quantity: number;
  weight?: number;
  notes?: string;
  source?: string;
  identified: boolean;
  tags?: string[];
  reason?: string;
}

export interface LootRemoveRequest {
  action: typeof LootActions.Remove;
  id: string;
  reason?: string;
}

/** Broadcast on loot/added and loot/updated — carries the full row. */
export interface LootRowPayload {
  item: LootItem;
}

/** Broadcast on loot/removed — clients drop the row from their local list. */
export interface LootRemovedPayload {
  id: string;
}

/**
 * Sent to the originator only on validation failure. `reason` is a short
 * machine-readable code; the client picks display copy. Possible reasons
 * today: `invalid_name`, `invalid_quantity`, `invalid_id`, `not_found`.
 */
export interface LootRejectedPayload {
  reason: string;
  id?: string;
}
